/**
 * Sub-aba "Visibilidade Aba": para cada uma das 16 abas da ficha, "Ambos" ou "Administradora", com um
 * ÚNICO "Salvar alterações" no rodapé (pedido do fundador em 07/10/2026; o legado tinha um por linha). Porte de `admin.html:1986-1989` e `8930-8994`; linha 128 do inventário.
 *
 * O MAPA LIDO SOBE PARA A TELA, porque o "Vínculo Produto Aba" mostra a visibilidade da aba
 * escolhida a partir dele (`visibilidadeAbasCarregadas`, `admin.html:8932`), e no legado os dois
 * compartilham a mesma variável.
 */

// Estado, memória e efeito do React.
import { useCallback, useEffect, useState } from 'react';
// As chamadas da área.
import { listarVisibilidadeAbas, salvarVisibilidadeAba } from '@studiolu/dados/areas/sistema';
// Sessão e fonte de dados.
import { usePainel } from '../../contexto.js';
// O grupo de rádio do admin, que o Chassi não tem.
import { Botao } from '@complexity/ui';
// O grupo de rádio do admin, que o Chassi não tem.
import { GrupoRadio } from '../../comum/grupo-radio.js';
// O status que some sozinho, e o que fica (o erro do legado não some).
import { LinhaDeStatus, useStatusTemporario } from '../../comum/status-temporario.js';
// Os rótulos das abas e das visibilidades, na ordem do legado.
import { PARAM_ABAS_LABEL, PARAM_VIS_LABEL, opcoesDe } from './catalogos.js';
// As peças visuais da área.
import {
  CabecalhoDeColunas,
  CartaoParam,
  LinhaParam,
  NotaSecao,
  TextoParam,
  type Carga,
  type PropsDaSubAba,
} from './pecas.js';

// As duas opções de rádio, na ordem do legado.
const OPCOES_DE_VISIBILIDADE = opcoesDe(PARAM_VIS_LABEL);

export function SubAbaVisibilidade({
  recarga,
  aoLerMapa,
  aoSalvarAba,
}: Pick<PropsDaSubAba, 'recarga'> & {
  // O mapa lido, para o Vínculo (`visibilidadeAbasCarregadas = {...}`).
  readonly aoLerMapa: (mapa: Readonly<Record<string, string>>) => void;
  // Uma aba salva, para o Vínculo (`visibilidadeAbasCarregadas[aba] = visibilidade`).
  readonly aoSalvarAba: (aba: string, visibilidade: string) => void;
}) {
  const { dados } = usePainel();
  // A lista começa carregando, como o "Carregando…" do legado (`admin.html:8936`).
  const [carga, setCarga] = useState<Carga<null>>({ tipo: 'carregando' });
  // O rádio marcado de cada aba: nasce do mapa lido, e muda com o clique.
  const [escolhas, setEscolhas] = useState<Readonly<Record<string, string>>>({});
  /**
   * O QUE O BANCO TEM, guardado à parte do que está na tela.
   *
   * É a diferença entre os dois que diz o que precisa ser gravado. Sem guardar o original, o botão
   * único não teria como saber o que mudou, e gravaria as dezesseis abas a cada clique.
   */
  const [original, setOriginal] = useState<Readonly<Record<string, string>>>({});
  // Trava o botão durante a gravação: clicar duas vezes gravaria a mesma coisa duas vezes.
  const [salvando, setSalvando] = useState(false);
  // `#param-visibilidade-status`: "Visibilidade salva." some em 3 s; o erro fica.
  const [status, mostrarStatus, fixarStatus] = useStatusTemporario();

  // `carregarVisibilidadeAbas` (`admin.html:8934-8946`).
  const carregar = useCallback(async () => {
    setCarga({ tipo: 'carregando' });
    // O status mora dentro da lista no legado, e some quando a lista é reescrita.
    fixarStatus('');
    const resultado = await listarVisibilidadeAbas(dados);
    if (!resultado.ok) {
      setCarga({ tipo: 'erro', mensagem: resultado.erro.mensagem });
      return;
    }
    // Linha sem visibilidade vale "ambos", como o `r.visibilidade || 'ambos'` do legado.
    const mapa: Record<string, string> = {};
    for (const linha of resultado.dados ?? []) mapa[linha.aba] = linha.visibilidade || 'ambos';
    // Os rádios redesenhados a partir do mapa: aba sem linha marca "Ambos" (`admin.html:8959`).
    const naTela = Object.fromEntries(Object.keys(PARAM_ABAS_LABEL).map((aba) => [aba, mapa[aba] || 'ambos']));
    setEscolhas(naTela);
    // O mesmo retrato guardado como "o que o banco tem": a partir daqui, diferença é alteração.
    setOriginal(naTela);
    aoLerMapa(mapa);
    setCarga({ tipo: 'pronta', dados: null });
  }, [dados, aoLerMapa, fixarStatus]);

  // Lê ao abrir a área e a cada clique na sub-aba.
  useEffect(() => {
    void carregar();
  }, [carregar, recarga]);

  // "Salvar" de uma linha (`admin.html:8980-8992`).
  /**
   * AS ABAS QUE MUDARAM, comparando o que está na tela com o que veio do banco.
   *
   * É isto que permite UM botão só: sem a comparação, "salvar tudo" gravaria as 16 abas a cada
   * clique, e 16 escritas para mudar uma linha é desperdício que o banco paga e que suja o
   * histórico de quem olhar depois.
   */
  const alteradas = Object.keys(PARAM_ABAS_LABEL).filter(
    (aba) => (escolhas[aba] ?? 'ambos') !== (original[aba] ?? 'ambos'),
  );

  /**
   * "Salvar alterações": UM botão para a tela inteira, por pedido do fundador em 07/10/2026
   * ("ajustar para colocar um unico botao salvar para salvar todas as alteracoes").
   *
   * MUDA O LEGADO DE PROPÓSITO, e ele decidiu: o admin atual tem um "Salvar" por linha
   * (`admin.html:8980-8992`), e mudar três abas custava três cliques e três idas ao banco.
   *
   * A GRAVAÇÃO É UMA DE CADA VEZ, e não todas em paralelo: a função de banco grava uma aba por
   * chamada, e disparar dezesseis juntas só troca a ordem dos erros, sem ficar mais rápido no que
   * importa. Na primeira falha a sequência PARA, e a mensagem diz em qual aba parou: continuar
   * depois de um erro deixaria a tela dizendo "salvo" com metade gravada.
   */
  const salvarTudo = useCallback(async () => {
    if (alteradas.length === 0) return;
    setSalvando(true);
    fixarStatus('');
    let gravadas = 0;
    for (const aba of alteradas) {
      // O rádio marcado, ou "ambos" quando nenhum está (`admin.html:8984`).
      const visibilidade = escolhas[aba] || 'ambos';
      const resultado = await salvarVisibilidadeAba(dados, aba, visibilidade, new Date().toISOString());
      if (!resultado.ok) {
        setSalvando(false);
        // O erro nomeia a ABA, e não só o motivo: com dezesseis linhas na tela, "erro ao salvar"
        // sozinho obriga a pessoa a adivinhar qual delas ficou para trás.
        fixarStatus(`Erro ao salvar "${PARAM_ABAS_LABEL[aba]}": ${resultado.erro.mensagem}`);
        return;
      }
      // O Vínculo Produto Aba lê este mapa, e ele precisa acompanhar cada aba gravada.
      aoSalvarAba(aba, visibilidade);
      // O original avança junto: assim o botão some para o que já foi, mesmo se o resto falhar.
      setOriginal((atuais) => ({ ...atuais, [aba]: visibilidade }));
      gravadas++;
    }
    setSalvando(false);
    mostrarStatus(gravadas === 1 ? 'Visibilidade salva.' : `${gravadas} visibilidades salvas.`);
  }, [alteradas, escolhas, dados, aoSalvarAba, mostrarStatus, fixarStatus]);

  return (
    <div className="flex flex-col gap-cx-4">
      <NotaSecao>Define quais opções de visibilidade ficam disponíveis pra cada aba, no vínculo produto x aba.</NotaSecao>
      {carga.tipo === 'carregando' && <TextoParam>Carregando…</TextoParam>}
      {carga.tipo === 'erro' && <TextoParam>{`Erro ao carregar: ${carga.mensagem}`}</TextoParam>}
      {carga.tipo === 'pronta' && (
        <>
          <CartaoParam testid="lista-visibilidade">
            {/* A coluna vazia do "Salvar" saiu junto com os botões de linha. */}
            <CabecalhoDeColunas>
              <span className="min-w-0 flex-1">Aba</span>
              <span className="min-w-0 flex-1">Visibilidade</span>
            </CabecalhoDeColunas>
            {Object.keys(PARAM_ABAS_LABEL).map((aba) => (
              <LinhaParam key={aba} data-aba={aba}>
                <span className="min-w-0 basis-full text-cx-sm [overflow-wrap:anywhere] md:flex-1 md:basis-0">
                  {PARAM_ABAS_LABEL[aba]}
                </span>
                <span className="min-w-0 basis-full md:flex-1 md:basis-0">
                  <GrupoRadio
                    nome={`visibilidade-aba-${aba}`}
                    opcoes={OPCOES_DE_VISIBILIDADE}
                    valor={escolhas[aba] ?? 'ambos'}
                    aoMudar={(valor) => setEscolhas((atuais) => ({ ...atuais, [aba]: valor }))}
                    rotuloDoGrupo={`Visibilidade de ${PARAM_ABAS_LABEL[aba]}`}
                  />
                </span>
              </LinhaParam>
            ))}
          </CartaoParam>

          {/*
            UM BOTÃO PARA A TELA INTEIRA, no rodapé, à direita: é onde a ação que fecha um
            formulário mora em toda plataforma grande, e é o último lugar por onde o olho passa
            depois de percorrer as dezesseis linhas.

            ELE DIZ QUANTAS ALTERAÇÕES VAI GRAVAR, e não só "Salvar": com dezesseis linhas na tela,
            a pessoa precisa saber se o que ela mexeu foi registrado antes de clicar. Com nada
            alterado ele fica desligado, e o texto explica por quê, em vez de aceitar um clique que
            não faria nada.
          */}
          <div className="flex flex-wrap items-center justify-end gap-cx-3">
            <span className="text-cx-sm text-texto-suave" data-testid="contagem-alteracoes">
              {alteradas.length === 0
                ? 'Nenhuma alteração para salvar.'
                : alteradas.length === 1
                  ? '1 alteração pendente.'
                  : `${alteradas.length} alterações pendentes.`}
            </span>
            <Botao
              variante="primario"
              onClick={() => void salvarTudo()}
              disabled={alteradas.length === 0 || salvando}
              data-testid="salvar-visibilidade"
            >
              {salvando ? 'Salvando...' : 'Salvar alterações'}
            </Botao>
          </div>

          <LinhaDeStatus texto={status} testid="status-visibilidade" />
        </>
      )}
    </div>
  );
}
